import React from 'react';
import {Translate, I18n} from 'react-redux-i18n';
import PropTypes from 'prop-types';
import {Link, useHistory} from 'react-router-dom';
import {Tooltip} from '@material-ui/core';
import {useDispatch} from 'react-redux';

import {Button, EntityIllustration, Icon} from 'front-onceforall-core';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';

import {togglePandaBox as togglePandaBoxAction} from '../../../actions/project';
import authenticationService from '../../../services/authentication/AuthenticationService';
import {MANAGE_PROJECT_PERMISSION, PROJECT_STATUS, status, projectType} from '../../../utils/constant';
import subscriptionService from '../../../services/subscription/SubscriptionService';
import documentService from '../../../services/document/DocumentService';
import DatetimeFormat from '../../datetime/DatetimeFormat';
import {captureError} from '../../../utils/log';
import {openOrSaveFile} from '../../../utils/http';

import ConfirmCloseProjectModal from '../ConfirmCloseProjectModal';

import Menu from './Menu';
import InternalNotesSidePanel from './InternalNotesSidePanel';
import UpgradeProjectButton from './UpgradeProjectButton';
import {pathResolver} from '../../../utils/basePathHelper';

function ProjectHeader(props) {
  const [baseUrl, setBaseUrl] = React.useState('');
  const [state, updateState] = React.useState({
    expanded: false,
    confirmCloseProjectModalIsOpen: false,
    internalNotesIsOpen: false,
    nominativeProjectCredits: 0,
    nbInternalNotes: 0
  });
  const dispatch = useDispatch();
  const history = useHistory();

  const roles = authenticationService.roles();
  const isConsultant = roles.isConsultant();
  const canManageProject = roles.hasPermission(MANAGE_PROJECT_PERMISSION);
  const isBackOfficeUser = roles.isBackOffice();

  const setState = update => updateState(current => ({...current, ...update}));

  const togglePandaBox = () => dispatch(togglePandaBoxAction(props.project.projectId));

  const getCredits = () => (
    subscriptionService.findSubscriptionByOrganisation(props.organisationId)
      .then(subscription => {
        setState({
          nominativeProjectCredits: subscription ?
            subscription.maximumNumberOfNominativeProjects - subscription.numberOfNominativeProjects : 0
        });
      })
      .catch(error => {
        captureError('Error while fetching subscription', error);
      })
  );

  React.useEffect(() => {
    getCredits();
  }, []);

  React.useEffect(() => {
    setBaseUrl(`/project/${props.project?.projectId}/intervention/${props.myIntervention?.interventionId}`);
  }, [props.project?.projectId, props.myIntervention?.interventionId]);

  const handleOpenConfirmCloseProjectModal = () => {
    setState({confirmCloseProjectModalIsOpen: true});
  };

  const handleCloseUploadModal = () => {
    setState({confirmCloseProjectModalIsOpen: false});
  };

  const handleCloseProject = () => {
    props.onSubmitCloseProject();
    handleCloseUploadModal();
  };

  const isOwner = () => props.project.ownerOrganisation.organisationId === props.organisationId;

  const handleOpenOrganisationsPanel = () => {
    if (props.project.projectStatus === PROJECT_STATUS.CLOSED || props.myIntervention.status === status.ENDED) {
      return;
    }

    props.onOpenOrganisationsPanel();
  };

  const handleInternalNotesPanelCallback = internalNotesIsOpen => () => {
    setState({internalNotesIsOpen});
  };

  const handleDownloadProjectStatus = () => {
    const data = {
      userId: props.user.id,
      interventionId: props.myIntervention.interventionId,
      locale: props.currentLocale
    };

    return documentService.createDocumentExport(data)
      .then(() => props.actions.addSuccess(<Translate value="message.exportRequestSuccess"/>))
      .catch(error => {
        captureError('documentService.createDocumentExport', error);
        props.actions.addError(<Translate value="error.generic"/>);
      });
  };

  const handleDownloadProjectOverviewReport = () => (
    authenticationService.authRedirect(
      documentService.getProjectOverviewReportDownloadUrl(
        props.myIntervention.interventionId,
        props.currentLocale
      )
    )
      .then(authUrl => openOrSaveFile(authUrl))
      .catch(error => {
        captureError('documentService.createDocumentExport', error);
        props.actions.addError(<Translate value="error.generic"/>);
      })
  );

  const renderPandaIcon = () => {
    if (
      !isOwner() ||
      history?.location?.pathname !== baseUrl ||
      props.project.country !== COUNTRY.FRANCE ||
      props.project.projectStatus !== PROJECT_STATUS.ACTIVE
    ) {
      return null;
    }

    return (
      <button
        type="button"
        onClick={togglePandaBox}
      >
        <img
          alt="icon panda"
          src={pathResolver('resources/img/icon_panda.png')}
          style={{width: '100%', maxWidth: 50}}
        />
      </button>
    );
  };

  const displayInternalNotesBadge = () => (
    <span
      data-id="badge-internal-notes"
      className="badge"
    >
      {state.nbInternalNotes > 99 ?
        '99+' : state.nbInternalNotes
      }
    </span>
  );

  const displayInternalNotesButton = () => (
    <button
      data-id="internal-notes-button"
      className="large inline-container bo"
      onClick={handleInternalNotesPanelCallback(true)}
    >
      <Translate value="project.internalNotes.title"/>
      {state.internalNotesLoading ?
        <Icon
          data-id="internal-notes-loader"
          icon="loader"
        /> :
        displayInternalNotesBadge()
      }
    </button>
  );

  const {project, myIntervention} = props;

  const closingDateTooltipTitle = (
    <>
      <Translate value="project.closingDate"/>: <DatetimeFormat
        value={project.closingDate}
        format="shortDate"
      />
    </>
  );

  const isClosed = project.projectStatus === PROJECT_STATUS.CLOSED;
  const isEnded = myIntervention.status === status.ENDED;
  const iAmProjectOwner = myIntervention.project.ownerOrganisation.organisationId === props.organisationId;
  const showUpgradeProject = isBackOfficeUser &&
      props.project.projectType === projectType.STANDARD &&
      iAmProjectOwner && !isClosed;

  return (
    <>
      <Link to={props.backButtonLink}>
        <button
          type="button"
          className="inline-container large link my-4"
        >
          <Icon
            icon="arrow-left"
            color="paragraph-light"
          />
          <Translate value={props.backButtonLabel}/>
        </button>
      </Link>
      <div className="d-flex justify-content-between">
        <div className="mb-4 flex-container flex-shrink-1">
          <EntityIllustration
            country={project.country}
            name={project.name}
          />
          <h1 className="m-0 ml-4">
            <strong style={{fontSize: '26px'}}>
              {project.internalReference ? `${project.internalReference} - ` : '' }
              {project.name}
            </strong>
          </h1>
          {isClosed &&
                  <span
                    data-id="closingDate"
                    className="ml-5"
                  >
                    <Tooltip title={closingDateTooltipTitle}>
                      <span className="no-wrap chip error">
                        <span>
                          <Translate value="project.projectClosedChip"/>
                        </span>
                      </span>
                    </Tooltip>
                  </span>
          }

        </div>
        <div className="mb-4 flex-container flex-shrink-0 justify-content-lg-end h-100">
          {renderPandaIcon()}
          {showUpgradeProject &&
                  <UpgradeProjectButton
                    projectId={project?.projectId}
                    disabled={state.nominativeProjectCredits < 1}
                  />
          }
          {isBackOfficeUser && displayInternalNotesButton()}
          {!isConsultant &&
                  <Button
                    size="large"
                    type="cta"
                    icon="plus"
                    data-id="add-organisation"
                    onClick={handleOpenOrganisationsPanel}
                    disabled={isClosed || isEnded}
                  >
                    {I18n.t('project.edit.addOrganisation')}
                  </Button>
          }
          <Menu
            baseUrl={baseUrl}
            showMenu={canManageProject && isOwner()}
            isClosed={isClosed}
            onOpenEditProjectPanel={props.onOpenEditProjectPanel}
            openConfirmCloseProjectModal={handleOpenConfirmCloseProjectModal}
            onDownloadProjectStatus={handleDownloadProjectStatus}
            onDownloadProjectOverviewReport={handleDownloadProjectOverviewReport}
          />
        </div>
      </div>
      {state.confirmCloseProjectModalIsOpen &&
        <ConfirmCloseProjectModal
          onConfirm={handleCloseProject}
          isOpen={state.confirmCloseProjectModalIsOpen}
          onClose={handleCloseUploadModal}
        />
      }
      <InternalNotesSidePanel
        isOpen={state.internalNotesIsOpen}
        onClose={handleInternalNotesPanelCallback(false)}
        projectId={project.projectId}
        setNbInternalNotesCallback={nbInternalNotes => setState({nbInternalNotes})}
      />
    </>
  );
}

ProjectHeader.propTypes = {
  project: PropTypes.object.isRequired,
  organisationId: PropTypes.number.isRequired,
  myIntervention: PropTypes.object.isRequired,
  onOpenOrganisationsPanel: PropTypes.func.isRequired,
  onOpenEditProjectPanel: PropTypes.func.isRequired,
  onSubmitCloseProject: PropTypes.func.isRequired,
  backButtonLink: PropTypes.string.isRequired,
  backButtonLabel: PropTypes.string.isRequired,
  currentLocale: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  actions: PropTypes.shape({
    addError: PropTypes.func.isRequired,
    addSuccess: PropTypes.func.isRequired
  }).isRequired
};

export default ProjectHeader;
